import React from "react";
//#region types
import { GenericFormDefinition } from "models/propTypes/genericFormProps";
import { FieldConstants, FormConstants } from "utils/constants";
import { StyleEnums } from "models/enums";
import { TranslationKeys } from "translations";
import { ObjectSchema } from "models/utilTypes/yupSchemaTypes";
//#endregion
//#region components
import useFloatingMessages from "../messages/useFloatingMessages";
import FormContextProvider from "./context/contextProvider";
import GenericFormInnerContainer from "./genericFormInnerContainer";
import { Form, Formik, FormikProps } from "formik";
import { Grid } from "@mui/material";
//#endregion
import clsx from "clsx";
import cssExports from "theme/styles/main.module.scss";
import { CSSHelper, UtilsHelper, FormHelper, LocalizationHelperInstance } from "utils/helpers";

/**
 * @deprecated This component is deprecated. Use <WintForm /> instead.
 *
 * Migration guide:
 * 1. Replace GenericForm with WintForm component
 * 2. Use useWintForm hook for form logic
 * 3. Use connect() function to bind form fields
 * 4. Use VerticalFormField wrapper for form fields
 *
 * @see WintForm - The replacement component
 * @see useWintForm - Hook for form state management
 * @see connect - Function to connect form fields
 * @see VerticalFormField - Wrapper for form fields
 * @see https://app.clickup.com/4523827/v/dc/4a1tk-21632/4a1tk-8552
 *
 * @example
 * // Old way (deprecated):
 * <GenericForm
 *   formRef={ref}
 *   defaultValues={values}
 *   validationSchema={schema}
 *   handleSubmit={handleSubmit}
 *   fields={fields}
 * />
 *
 * @example
 * // New way (recommended):
 * const { wintFormProps, connect } = useWintForm({
 *   formRef: ref,
 *   defaultValues: values,
 *   validationSchema: schema,
 *   handleSubmit: handleSubmit,
 * });
 *
 * <WintForm {...wintFormProps}>
 *   <VerticalFormField Component={TextInput} {...connect("fieldName")} />
 * </WintForm>
 *
 * @see SupportTicketCreationForm - Example of successful migration
 */
function GenericForm<T extends Record<string, any>>(props: GenericFormDefinition<T>) {
  let innerRef: FormikProps<T> | null;

  const { enqueueMessage } = useFloatingMessages();

  const showValidationErrorMessage = (validationFailMessage?: string) => {
    if (validationFailMessage)
      enqueueMessage(
        LocalizationHelperInstance.getFormattedMessage(validationFailMessage),
        StyleEnums.DisplayType.Danger,
        true
      );
  };

  const validateForm = async (validationFailMessage?: string, validationSchema?: ObjectSchema<T>) => {
    if (innerRef === null) {
      return false;
    }

    await UtilsHelper.sleep(FieldConstants.DebounceTime); //Wait if debounce inputs are still updating
    const values = innerRef.values;

    let isValid = true;
    if (validationSchema) {
      try {
        await FormHelper.validateYupSchema(values, validationSchema);
        isValid = true;
      } catch (errors) {
        const formikErrors = FormHelper.yupToFormErrors(errors);
        innerRef.setErrors(formikErrors);
        innerRef.setTouched(formikErrors);
        isValid = false;
      }
    } else {
      const validateForm = innerRef.validateForm;

      const errors = await validateForm();
      const touched = await FormHelper.getTouchedObj(errors, innerRef.touched);
      innerRef.setTouched(touched);
      isValid = Object.keys(errors).length === 0;
    }

    if (!isValid) {
      innerRef.setSubmitting(false);
      showValidationErrorMessage(validationFailMessage);
      throw new Error("validation failed");
    }
    return isValid;
  };

  const submitForm = async (
    submitFunc?: (values: any, data?: any) => Promise<any>,
    validationSchema?: ObjectSchema<T>,
    noValidation?: boolean,
    data?: any,
    customValidationFailMessage?: string
  ) => {
    if (innerRef) {
      await innerRef.submitForm();
      innerRef.setSubmitting(true);
      innerRef.setStatus(FormConstants.Submitted);

      const isValid =
        noValidation ||
        (await validateForm(
          customValidationFailMessage ?? TranslationKeys.Validations.FormSubmitFailed,
          validationSchema
        ));

      const values = innerRef.values;

      if (isValid) {
        try {
          const res = await (submitFunc ?? props.handleSubmit ?? (async () => {}))(values, data);
          props.onSubmitSuccess && props.onSubmitSuccess(res);
          return res;
        } catch (error) {
          throw error;
        } finally {
          innerRef.setSubmitting(false);
        }
      }
    } else return null;
  };

  React.useImperativeHandle(props.formRef, () => ({
    submit: submitForm,
    getValues: () => {
      return innerRef?.values;
    },
    validate: validateForm,
    setFieldValue: (name: string, value: any) => {
      innerRef?.setFieldValue(name, value);
    },
    setFieldError: (name: string, error: string | undefined) => {
      innerRef?.setFieldError(name, error);
    },
    setValues: (values: any) => {
      innerRef?.setValues({ ...innerRef.values, ...values }, true);
    },
    getUpdatedValues: () => {
      const values = innerRef?.values;
      const updatedValues: Record<string, any> = {};
      if (values) {
        const allKeys = Object.keys(props.defaultValues);
        Object.keys(values).forEach((key) => {
          if (!allKeys.includes(key)) allKeys.push(key);
        });

        allKeys.forEach((key) => {
          const value = values ? values[key] : undefined;
          if (props.defaultValues[key] !== value) {
            updatedValues[key] = value;
          }
        });
      }
      return updatedValues;
    },
    getTouched: () => innerRef?.touched,
    isDirty: () => innerRef?.dirty ?? false,
  }));

  const assignRef = (instance: FormikProps<T> | null) => {
    if (instance) innerRef = instance;
  };

  return (
    <Formik
      innerRef={assignRef}
      enableReinitialize
      onSubmit={() => new Promise((resolve) => resolve(null))}
      initialValues={props.defaultValues}
      validationSchema={props.validationSchema}
      validateOnMount
      validateOnBlur={false}
      validateOnChange
    >
      <Form className={props.isReadOnly ? cssExports.readOnlyForm : ""}>
        <Grid
          container
          className={
            props.invertedDisplayType
              ? clsx(cssExports.invertedInputForm, CSSHelper.getClassByDisplayType(props.invertedDisplayType))
              : ""
          }
        >
          <FormContextProvider>
            <GenericFormInnerContainer<T> {...props} submitForm={submitForm} initialValues={props.defaultValues} />
          </FormContextProvider>
        </Grid>
      </Form>
    </Formik>
  );
}

export default GenericForm;
